import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/auth/api";

// ==============================
// ✅ FETCH CREATED
// ==============================
export const fetchReferralCreateList = createAsyncThunk(
  "referral/fetchCreate",
  async ({ page = 1, search = "", fetchAll = false }, { rejectWithValue, getState }) => {
    try {
      const state = getState().referralData;
      const { pageSize, createdCache } = state;

      const cacheKey = `create_${page}_${search}_${pageSize}_${fetchAll}`;

      if (createdCache[cacheKey]) {
        return { data: createdCache[cacheKey], page, search, pageSize, fromCache: true };
      }

      let url = `/account/panel/ReferalList`;

      if (!fetchAll) {
        url += `?page=${page}&page_size=${pageSize}`;
      } else {
        url += `?page=1&page_size=10000`;
      }

      if (search) url += `&search=${search}`;

      const res = await api.get(url);

      return { data: res.data, page, search, pageSize, fromCache: false, cacheKey };

    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// ==============================
// ✅ FETCH USED
// ==============================
export const fetchReferralUsedList = createAsyncThunk(
  "referral/fetchUsed",
  async ({ page = 1, search = "", fetchAll = false }, { rejectWithValue, getState }) => {
    try {
      const state = getState().referralData;
      const { pageSize, usedCache } = state;

      const cacheKey = `used_${page}_${search}_${pageSize}_${fetchAll}`;

      if (usedCache[cacheKey]) {
        return { data: usedCache[cacheKey], page, search, pageSize, fromCache: true };
      }

      let url = `/account/panel/ReferalList/used`;

      if (!fetchAll) {
        url += `?page=${page}&page_size=${pageSize}`;
      } else {
        url += `?page=1&page_size=10000`;
      }

      if (search) url += `&search=${search}`;

      const res = await api.get(url);

      return { data: res.data, page, search, pageSize, fromCache: false, cacheKey };

    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// ==============================
// ✅ SLICE
// ==============================
const ReferralSlice = createSlice({
  name: "referralData",
  initialState: {
    // 🔹 separate data
    createdList: { result: [], pagination: {} },
    usedList: { result: [], pagination: {} },

    // 🔹 pagination
    createdPage: 1,
    usedPage: 1,

    pageSize: 10,
    search: "",

    // 🔹 separate cache
    createdCache: {},
    usedCache: {},

    status: "idle",
    error: null,
  },

  reducers: {
    setPageSize: (state, action) => {
      state.pageSize = action.payload;

      // clear both caches
      state.createdCache = {};
      state.usedCache = {};

      state.createdPage = 1;
      state.usedPage = 1;
    },

    setCreatedPage: (state, action) => {
      state.createdPage = action.payload;
    },

    setUsedPage: (state, action) => {
      state.usedPage = action.payload;
    },

    clearCache: (state) => {
      state.createdCache = {};
      state.usedCache = {};
    },
  },

  extraReducers: (builder) => {
    builder

      // =========================
      // ✅ CREATED
      // =========================
      .addCase(fetchReferralCreateList.pending, (state) => {
        state.status = "loading";
      })

      .addCase(fetchReferralCreateList.fulfilled, (state, action) => {
        const { data, page, cacheKey, fromCache } = action.payload;

        if (!fromCache) {
          state.createdCache[cacheKey] = data;
        }

        state.createdList = data;
        state.createdPage = page;
        state.status = "succeeded";
      })

      .addCase(fetchReferralCreateList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })


      // =========================
      // ✅ USED
      // =========================
      .addCase(fetchReferralUsedList.pending, (state) => {
        state.status = "loading";
      })

      .addCase(fetchReferralUsedList.fulfilled, (state, action) => {
        const { data, page, cacheKey, fromCache } = action.payload;

        if (!fromCache) {
          state.usedCache[cacheKey] = data;
        }

        state.usedList = data;
        state.usedPage = page;
        state.status = "succeeded";
      })

      .addCase(fetchReferralUsedList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  setPageSize,
  setCreatedPage,
  setUsedPage
} = ReferralSlice.actions;

export default ReferralSlice.reducer;